import { useRef, useState, useCallback } from "react";
import { Reveal } from "./Reveal";
import { MoveHorizontal } from "lucide-react";

const CompareSlider = ({ src, label, testid }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(2, Math.min(98, p));
    setPos(p);
  }, []);

  const onMove = (e) => {
    if (!dragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    update(x);
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className="relative aspect-[4/3] overflow-hidden bg-fr-surface border border-white/10 select-none cursor-ew-resize"
      onMouseDown={(e) => { dragging.current = true; update(e.clientX); }}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onMouseMove={onMove}
      onTouchStart={(e) => { dragging.current = true; update(e.touches[0].clientX); }}
      onTouchEnd={() => (dragging.current = false)}
      onTouchMove={onMove}
    >
      {/* After (full color) */}
      <img src={src} alt="after" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute bottom-3 right-3 z-20 fr-mono text-[10px] tracking-widest uppercase bg-fr-blue px-2 py-1">After</span>

      {/* Before (treated) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={src}
          alt="before"
          draggable={false}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: ref.current ? ref.current.offsetWidth : "100%", filter: "grayscale(1) brightness(0.55) contrast(1.1) sepia(0.25)" }}
        />
        <span className="absolute bottom-3 left-3 z-20 fr-mono text-[10px] tracking-widest uppercase bg-black/80 border border-white/20 px-2 py-1">Before</span>
      </div>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 z-10 w-0.5 bg-white" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white text-fr-black flex items-center justify-center shadow-lg">
          <MoveHorizontal size={18} />
        </div>
      </div>

      <span className="absolute top-3 left-3 z-20 fr-mono text-[10px] tracking-widest uppercase text-white/90">{label}</span>
    </div>
  );
};

const items = [
  { src: "https://images.unsplash.com/photo-1558916793-bd18987255bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "House Wash", testid: "compare-1" },
  { src: "https://images.unsplash.com/photo-1662908267813-1659a58096e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "Deck Restore", testid: "compare-2" },
];

export const BeforeAfter = () => (
  <section id="results" className="py-24 sm:py-32 bg-fr-black grain relative" data-testid="results-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <span className="fr-mono text-xs tracking-[0.3em] uppercase text-fr-blue-light">/ The transformation</span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
            Drag to see the difference.
          </h2>
        </div>
        <p className="text-white/50 max-w-sm">Real results that boost curb appeal and protect your home. Slide across each frame.</p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6">
        {items.map((it, i) => (
          <Reveal key={it.testid} delay={i * 0.1}>
            <CompareSlider {...it} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
